import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useBolComplianceReport } from '../../hooks/useBolComplianceReport';
import { formatAge } from '../../utils/formatAge';

interface Facility {
  id: number;
  name: string;
}

interface BolComplianceReportModalProps {
  open: boolean;
  onClose: () => void;
  facilities: Facility[];
  userId: string;
  jwtToken: string;
  websocketUrl: string;
}

const BolComplianceReportModal: React.FC<BolComplianceReportModalProps> = ({
  open,
  onClose,
  facilities,
  userId,
  jwtToken,
  websocketUrl,
}) => {
  const [selectedFacilityId, setSelectedFacilityId] = useState<number | ''>('');

  const {
    isGenerating,
    cacheStatus,
    error,
    checkCache,
    generateReport,
    resetState,
  } = useBolComplianceReport({ userId, jwtToken, websocketUrl, onDownload: onClose });

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedFacilityId('');
      resetState();
    }
  }, [open, resetState]);

  // Check cache when facility changes
  useEffect(() => {
    if (open && selectedFacilityId !== '') {
      checkCache(selectedFacilityId);
    }
  }, [open, selectedFacilityId, checkCache]);

  const handleDownload = () => {
    if (selectedFacilityId === '') return;
    generateReport(selectedFacilityId, false);
  };

  const handleForceNew = () => {
    if (selectedFacilityId === '') return;
    generateReport(selectedFacilityId, true);
  };

  const handleClose = () => {
    if (!isGenerating) {
      onClose();
    }
  };


  const renderCacheStatus = () => {
    if (selectedFacilityId === '') return null;

    switch (cacheStatus.status) {
      case 'checking':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Checking for cached report...</Typography>
          </Box>
        );
      case 'cached':
        return (
          <Box sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
              Cached report available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generated {formatAge(cacheStatus.age_seconds, cacheStatus.cached_at)}
              {cacheStatus.row_count !== undefined && ` (${cacheStatus.row_count} rows)`}
            </Typography>
          </Box>
        );
      case 'processing':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200', borderRadius: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="info.main">A report is currently being generated...</Typography>
          </Box>
        );
      case 'none':
        return null;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Download BOL Compliance Report
        <IconButton onClick={handleClose} disabled={isGenerating} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel id="facility-select-label">Facility</InputLabel>
          <Select
            labelId="facility-select-label"
            value={selectedFacilityId}
            label="Facility"
            onChange={(e) => setSelectedFacilityId(e.target.value as number)}
            disabled={isGenerating}
          >
            {facilities.map((facility) => (
              <MenuItem key={facility.id} value={facility.id}>
                ({facility.id}) {facility.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ mt: 2 }}>
          {isGenerating ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Generating report...</Typography>
            </Box>
          ) : (
            <>
              {renderCacheStatus()}
              {cacheStatus.status === 'cached' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Force a new report to query the database for the latest information. The cached report was generated {formatAge(cacheStatus.age_seconds, cacheStatus.cached_at)}.
                </Alert>
              )}
            </>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {cacheStatus.status === 'cached' && !isGenerating && (
          <Button
            variant="outlined"
            onClick={handleForceNew}
            disabled={selectedFacilityId === '' || isGenerating}
          >
            Force New Report
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={selectedFacilityId === '' || isGenerating}
        >
          Download Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BolComplianceReportModal;
